import { CookieBanner } from '@/components/cookie-banner'
import { GameSection } from '@/components/game-section'
import { GameEmailVerificationGuard } from '@/components/game-email-verification-guard'
import { Navigation } from '@/components/navigation'
import { ScrollToTop } from '@/components/scroll-to-top'
import { ThemeProvider } from '@/components/theme-provider'
import { SocialChatDock } from '@/components/social'
import { Toaster } from '@/components/ui/sonner'
import { TournamentHall } from '@/components/tournament/hall'
import { TournamentPage } from '@/components/tournament/page'
import { AuthProvider } from '@/context/auth'
import { AccountTimeZoneProvider } from '@/hooks/use-account-time-zone'
import { useReloadInstalledAppOnNewDeploy } from '@/hooks/use-installed-app-update'
import { useWebPushStartupReconciliation } from '@/hooks/use-web-push-startup-reconciliation'
import {
  type AccountSessionRevalidationDetail,
  ACCOUNT_ENDED_STORAGE_KEY,
  ACCOUNT_SESSION_REVALIDATE_EVENT,
} from '@/lib/account-transition-signal'
import { resolveBrowserIdentity } from '@/lib/auth-identity'
import {
  clearAccountCacheIfCurrent,
  createSessionRevalidationGuard,
  reconcileAccountCacheForSessionTruth,
  replaceAccountQueryScope,
} from '@/lib/auth-session-revalidation'
import { DataSettingProvider } from '@/lib/data-setting-context'
import { BlogArticlePage } from '@/pages/blog-article-page'
import { BlogListPage } from '@/pages/blog-list-page'
import { CareerPage } from '@/pages/career-page'

import { ComparisonPlayPage } from '@/pages/comparison-play'
import { ContactPage } from '@/pages/contact-page'
import { DashboardPage } from '@/pages/dashboard-page'
import { DemoPage } from '@/pages/demo-page'
import { FaqPage } from '@/pages/faq-page'
import { ForgetPasswordPage } from '@/pages/forget-password-page'
import { ForumNewThreadPage } from '@/pages/forum-new-thread-page'
import { ForumPage } from '@/pages/forum-page'
import { LobbyPage } from '@/pages/lobby-page'
import { RoomRulesPage } from '@/pages/room-rules-page'
import { ForumThreadPage } from '@/pages/forum-thread-page'
import { GameArchivePage } from '@/pages/game-archive-page'
import { GamePage } from '@/pages/game-page'
import { GameHomepagePage } from '@/pages/game-homepage-page'
import { GameRulesPage } from '@/pages/game-rules-page'
import { HomePage } from '@/pages/home-page'
import { LeaderboardPage } from '@/pages/leaderboard-page'
import { CookiePolicyPage } from '@/pages/legal/cookie-policy'
import { PrivacyPolicyPage } from '@/pages/legal/privacy-policy'
import { TermsOfServicePage } from '@/pages/legal/terms-of-service'
import { DesktopGamePage } from '@/pages/desktop-game'
import { NotFoundPage } from '@/pages/not-found-page'
import { PublicPlayerProfilePage } from '@/pages/public-player-profile-page'
import { RoleIndexPage } from '@/pages/role-index-page'
import { LegacyPlayRouteRedirectPage } from '@/pages/legacy-play-route-redirect-page'
import { ResetPasswordPage } from '@/pages/reset-password-page'
import { VerifyEmailPage } from '@/pages/verify-email-page'
import { ReviewPage } from '@/pages/review-page'
import { RoomPage } from '@/pages/room-page'
import { SeoLandingPage } from '@/pages/seo-landing-page'
import { SignInPage } from '@/pages/signin-page'
import { SignUpPage } from '@/pages/signup-page'
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import {
  getArenaUrl,
  isArenaHost,
  isArenaNotificationSetupHost,
} from './lib/arena'
import { isLobbyEnabled } from './lib/lobby'
import { authClient } from './lib/auth'
import { platform } from './lib/platform'
import { isSteamClient } from './lib/steam'
import { trpcClient, TRPCProvider, useTRPC } from './lib/trpc'
import {
  getLegacyQueryLanguage,
  getPathLanguage,
  getPathLanguagePrefix,
  withLanguagePath,
} from './lib/url-language-utils'
import { UserStream } from './lib/user-stream'
import { SocialWorkspaceProvider } from './social/social-provider'
import { TRPCClientError } from '@trpc/client'
import { returnTargetFrom, verifyEmailUrlReturningTo } from './lib/return-target'

function AccountDataProviders({ children }: { children: ReactNode }) {
  return (
    <AccountTimeZoneProvider>
      <DataSettingProvider>{children}</DataSettingProvider>
    </AccountTimeZoneProvider>
  )
}

function makeQueryClient() {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error) => {
        if (
          typeof window === 'undefined'
          || !(error instanceof TRPCClientError)
          || error.message !== 'email_verification_required'
        ) {
          return
        }
        const prefix = getPathLanguagePrefix(window.location.pathname) ?? ''
        if (window.location.pathname === `${prefix}/verify-email`)
          return
        window.location.assign(
          `${prefix}${verifyEmailUrlReturningTo(returnTargetFrom(window.location))}`,
        )
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined
const BROWSER_SESSION_REVALIDATION_INTERVAL_MS = 15_000

function getQueryClient() {
  if (typeof window === 'undefined') {
    return makeQueryClient()
  }
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient()
  }
  return browserQueryClient
}

function ArenaRedirect({ to, children }: { to?: string, children: ReactNode }) {
  const arenaUrl = getArenaUrl()
  useEffect(() => {
    if (arenaUrl) {
      const path = to ?? window.location.pathname
      window.location.replace(
        `${arenaUrl}${path}${window.location.search}${window.location.hash}`,
      )
    }
  }, [arenaUrl, to])
  return arenaUrl ? null : <>{children}</>
}

function ArenaLauncherRedirect() {
  const steamClient = isSteamClient()
  useEffect(() => {
    if (steamClient)
      return
    const arenaUrl = getArenaUrl()
    const { pathname, search, hash } = window.location
    const language = getPathLanguage(pathname) ?? getLegacyQueryLanguage(search) ?? 'en'
    const launcherPath = withLanguagePath('/new', language)
    const params = new URLSearchParams(search)
    params.delete('lang')
    params.delete('research')
    if (arenaUrl) {
      const query = params.toString()
      window.location.replace(`${arenaUrl}${launcherPath}${query ? `?${query}` : ''}${hash}`)
    }
    else {
      params.set('arena', '')
      window.location.replace(
        `${window.location.origin}${launcherPath}?${params.toString()}${hash}`,
      )
    }
  }, [steamClient])
  return steamClient ? <GameSection /> : null
}

function LegacyLauncherRedirect() {
  const { search, hash } = useLocation()
  return <Navigate to={`/new${search}${hash}`} replace />
}

function DemoToArenaReviewRedirect() {
  return (
    <ArenaRedirect to={window.location.pathname.replace(/\/demo(?=\/|$)/, '/review')}>
      <DemoPage />
    </ArenaRedirect>
  )
}

function GrpoToHomeRedirect() {
  const { search, hash } = useLocation()
  return <Navigate to={`/${search}${hash}`} replace />
}

function RouteNavigation({
  minimal,
  notificationSetupEnabled,
}: {
  minimal: boolean
  notificationSetupEnabled: boolean
}) {
  const { pathname } = useLocation()
  if (pathname.startsWith('/village-preview/')) {
    return null
  }

  return (
    <Navigation
      minimal={minimal || pathname === '/tournaments' || pathname.startsWith('/tournaments/')}
      notificationSetupEnabled={notificationSetupEnabled}
    />
  )
}

function NormalizeLegacyQueryLanguage({ children }: { children: ReactNode }) {
  const location = useLocation()

  useEffect(() => {
    if (isSteamClient())
      return
    const requested = getLegacyQueryLanguage(location.search)
    if (!requested)
      return
    const destination = new URL(window.location.href)
    destination.pathname = withLanguagePath(
      destination.pathname,
      getPathLanguage(destination.pathname) ?? requested,
    )
    destination.searchParams.delete('lang')
    if (destination.href !== window.location.href)
      window.location.replace(destination.href)
  }, [location.pathname, location.search, location.hash])

  return <>{children}</>
}

function AppContent({
  replaceAccountQueryClient,
}: {
  replaceAccountQueryClient: (
    seed?: (queryClient: QueryClient) => void,
  ) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [identityRevalidating, setIdentityRevalidating] = useState(false)
  const [validatedSessionIdentity, setValidatedSessionIdentity] = useState<
    { userId: string, sessionId: string } | null | undefined
  >()
  const validatedAuthObservation = useRef<string | null | undefined>()
  const accountCacheOwnerUserId = useRef<string | undefined>()
  const identityRevalidationInFlight = useRef(false)
  const sessionRevalidationGuard = useRef(
    createSessionRevalidationGuard(),
  ).current
  const commitValidatedIdentity = useCallback(
    (
      generation: number,
      truth: { userId: string, sessionId: string },
    ): boolean => {
      if (!sessionRevalidationGuard.isCurrent(generation))
        return false
      accountCacheOwnerUserId.current = truth.userId
      setValidatedSessionIdentity(truth)
      return true
    },
    [sessionRevalidationGuard],
  )
  const finishIdentityRevalidation = useCallback(
    (generation: number): void => {
      if (!sessionRevalidationGuard.isCurrent(generation))
        return
      identityRevalidationInFlight.current = false
      setIdentityRevalidating(false)
    },
    [sessionRevalidationGuard],
  )
  const {
    data: session,
    error: sessionError,
    isPending: isSessionPending,
  } = authClient.useSession()
  const { data: user, isPending: isUserPending } = useQuery({
    ...trpc.userRouter.getUserInfo.queryOptions(),
    enabled:
      !identityRevalidating
      && validatedSessionIdentity !== null
      && (!!session || platform.skipSessionGate),
  })
  const sessionTruthMutation = useMutation(
    trpc.webPushRouter.sessionTruth.mutationOptions(),
  )
  const sessionTruthRef = useRef(sessionTruthMutation.mutateAsync)
  sessionTruthRef.current = sessionTruthMutation.mutateAsync
  const observedAuthIdentity = platform.skipSessionGate
    ? user?.id
      ? `steam:${user.id}`
      : null
    : session
      ? `browser:${session.user.id}:${session.session.id}`
      : null

  const revalidateAccountIdentity = useCallback(
    async (clearFirst = false) => {
      if (!clearFirst && identityRevalidationInFlight.current)
        return false
      const generation = sessionRevalidationGuard.begin()
      const previousCacheOwnerUserId = accountCacheOwnerUserId.current
      const isCurrent = () => sessionRevalidationGuard.isCurrent(generation)
      const clearIfCurrent = () =>
        clearAccountCacheIfCurrent({
          guard: sessionRevalidationGuard,
          generation,
          cancel: () => queryClient.cancelQueries(),
          clear: () => queryClient.clear(),
        })
      identityRevalidationInFlight.current = true
      if (clearFirst) {
        setIdentityRevalidating(true)
        validatedAuthObservation.current = observedAuthIdentity
        setValidatedSessionIdentity(null)
        if (!(await clearIfCurrent()))
          return false
      }
      try {
        const truth = await sessionTruthRef.current()
        if (!isCurrent())
          return false
        validatedAuthObservation.current = observedAuthIdentity
        if (!truth) {
          setValidatedSessionIdentity(null)
          if (!(await clearIfCurrent()))
            return false
          if (
            previousCacheOwnerUserId !== undefined
            && previousCacheOwnerUserId !== null
            && isCurrent()
          ) {
            replaceAccountQueryClient()
          }
          return false
        }

        if (
          !clearFirst
          && previousCacheOwnerUserId !== undefined
          && truth.userId !== previousCacheOwnerUserId
        ) {
          setValidatedSessionIdentity(null)
        }
        const cacheResult = await reconcileAccountCacheForSessionTruth({
          guard: sessionRevalidationGuard,
          generation,
          previousUserId: previousCacheOwnerUserId,
          observedUserId: user?.id,
          truthUserId: truth.userId,
          cacheAlreadyCleared: clearFirst,
          cancel: () => queryClient.cancelQueries(),
          clear: () => queryClient.clear(),
          loadUser: async () => {
            const probeQueryClient = makeQueryClient()
            try {
              return await probeQueryClient.fetchQuery(
                trpc.userRouter.getUserInfo.queryOptions(),
              )
            }
            finally {
              probeQueryClient.clear()
            }
          },
          writeUser: refreshedUser =>
            queryClient.setQueryData(
              trpc.userRouter.getUserInfo.queryOptions().queryKey,
              refreshedUser,
            ),
          replaceUserCache: refreshedUser =>
            replaceAccountQueryClient((nextQueryClient) => {
              nextQueryClient.setQueryData(
                trpc.userRouter.getUserInfo.queryOptions().queryKey,
                refreshedUser,
              )
            }),
        })
        if (cacheResult === 'stale')
          return false
        if (cacheResult === 'mismatch') {
          setValidatedSessionIdentity(null)
          await clearIfCurrent()
          return false
        }
        if (cacheResult === 'replaced')
          return true
        return commitValidatedIdentity(generation, truth)
      }
      catch {
        if (!isCurrent())
          return false
        validatedAuthObservation.current = observedAuthIdentity
        setValidatedSessionIdentity(null)
        await clearIfCurrent()
        return false
      }
      finally {
        finishIdentityRevalidation(generation)
      }
    },
    [
      observedAuthIdentity,
      commitValidatedIdentity,
      finishIdentityRevalidation,
      queryClient,
      replaceAccountQueryClient,
      sessionRevalidationGuard,
      trpc.userRouter,
      user?.id,
    ],
  )

  const previousObservedAuthIdentity = useRef<string | null | undefined>()
  useEffect(() => {
    if (previousObservedAuthIdentity.current === undefined) {
      previousObservedAuthIdentity.current = observedAuthIdentity
      if (observedAuthIdentity)
        void revalidateAccountIdentity(true)
      return
    }
    if (previousObservedAuthIdentity.current === observedAuthIdentity)
      return
    previousObservedAuthIdentity.current = observedAuthIdentity
    void revalidateAccountIdentity(true)
  }, [observedAuthIdentity, revalidateAccountIdentity])

  const gateCachedAccountIdentity = useCallback(() => {
    const generation = sessionRevalidationGuard.begin()
    identityRevalidationInFlight.current = false
    setIdentityRevalidating(true)
    validatedAuthObservation.current = observedAuthIdentity
    setValidatedSessionIdentity(null)
    void clearAccountCacheIfCurrent({
      guard: sessionRevalidationGuard,
      generation,
      cancel: () => queryClient.cancelQueries(),
      clear: () => queryClient.clear(),
    })
  }, [observedAuthIdentity, queryClient, sessionRevalidationGuard])

  const probedSession = validatedSessionIdentity !== undefined
  const sessionUserId = probedSession
    ? (validatedSessionIdentity?.userId ?? null)
    : (session?.user.id ?? null)
  const exactBrowserSessionId = probedSession
    ? (validatedSessionIdentity?.sessionId ?? null)
    : (session?.session.id ?? null)
  const observedIdentityMovedSinceValidation
    = probedSession
      && validatedSessionIdentity !== null
      && validatedAuthObservation.current !== observedAuthIdentity
  const identity = resolveBrowserIdentity({
    skipSessionGate: platform.skipSessionGate,
    sessionPending: !probedSession && isSessionPending,
    sessionError: !probedSession && !!sessionError,
    sessionInvalidated: validatedSessionIdentity === null,
    userPending: isUserPending,
    revalidating: identityRevalidating || observedIdentityMovedSinceValidation,
    sessionUserId,
    browserSessionId: exactBrowserSessionId,
    user,
  })
  const { authResolved, userInfo, browserSessionId } = identity
  const visibleSessionPollingEnabled
    = !!session?.session.id
      || !!validatedSessionIdentity?.sessionId
      || (platform.skipSessionGate && !!user?.id)

  useEffect(() => {
    if (
      platform.skipSessionGate
      || validatedSessionIdentity !== undefined
      || !sessionError
    ) {
      return
    }
    void revalidateAccountIdentity(true)
  }, [revalidateAccountIdentity, sessionError, validatedSessionIdentity])

  useEffect(() => {
    const revalidateVisibleIdentity = () => {
      if (document.visibilityState !== 'visible')
        return
      void revalidateAccountIdentity()
    }
    window.addEventListener('focus', revalidateVisibleIdentity)
    document.addEventListener('visibilitychange', revalidateVisibleIdentity)
    const interval = window.setInterval(() => {
      if (visibleSessionPollingEnabled)
        revalidateVisibleIdentity()
    }, BROWSER_SESSION_REVALIDATION_INTERVAL_MS)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', revalidateVisibleIdentity)
      document.removeEventListener(
        'visibilitychange',
        revalidateVisibleIdentity,
      )
    }
  }, [revalidateAccountIdentity, visibleSessionPollingEnabled])

  useEffect(() => {
    const clearEndedAccount = (event: StorageEvent) => {
      if (event.key !== ACCOUNT_ENDED_STORAGE_KEY)
        return
      gateCachedAccountIdentity()
    }
    const revalidateChangedSession = (event: Event) => {
      const complete = (event as CustomEvent<AccountSessionRevalidationDetail>)
        .detail
        ?.complete
      void revalidateAccountIdentity(true).then((sessionIsValid) => {
        complete?.(sessionIsValid)
      })
    }
    window.addEventListener('storage', clearEndedAccount)
    window.addEventListener(
      ACCOUNT_SESSION_REVALIDATE_EVENT,
      revalidateChangedSession,
    )
    return () => {
      window.removeEventListener('storage', clearEndedAccount)
      window.removeEventListener(
        ACCOUNT_SESSION_REVALIDATE_EVENT,
        revalidateChangedSession,
      )
    }
  }, [gateCachedAccountIdentity, revalidateAccountIdentity])

  const arena = isArenaHost()
  const notificationSetupEnabled = isArenaNotificationSetupHost()
  const lobbyEnabled = isLobbyEnabled()
  const registeredUserId
    = userInfo && !userInfo.isAnonymous ? userInfo.id : null

  useWebPushStartupReconciliation(
    registeredUserId,
    browserSessionId,
    notificationSetupEnabled && authResolved,
  )
  useReloadInstalledAppOnNewDeploy()

  const content = (
    <BrowserRouter
      basename={
        typeof window !== 'undefined'
          ? (getPathLanguagePrefix(window.location.pathname) ?? undefined)
          : undefined
      }
    >
      <NormalizeLegacyQueryLanguage>
        <AuthProvider
          userInfo={userInfo}
          authResolved={authResolved}
          browserSessionId={browserSessionId}
        >
          <AccountDataProviders>
            <ScrollToTop />
            <div
              className="min-h-screen bg-background text-foreground"
              data-arena={arena ? 'true' : undefined}
            >
              <RouteNavigation
                minimal={arena}
                notificationSetupEnabled={notificationSetupEnabled}
              />
              {arena ? (
                <Routes>
                  <Route path="/" element={<GameHomepagePage />} />
                  <Route
                    path="/new"
                    element={(
                      <GameEmailVerificationGuard>
                        <GamePage minimal />
                      </GameEmailVerificationGuard>
                    )}
                  />
                  <Route path="/play" element={<LegacyLauncherRedirect />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route
                    path="/players/:username"
                    element={<PublicPlayerProfilePage />}
                  />
                  <Route path="/play/rule" element={<GameRulesPage />} />
                  <Route path="/play/roles" element={<RoleIndexPage />} />
                  {
                  }
                  <Route path="/games" element={<GameArchivePage />} />
                  <Route path="/tournaments/hall" element={<TournamentHall />} />
                  <Route path="/tournaments" element={<TournamentPage />} />
                  <Route path="/tournaments/:editionId" element={<TournamentPage />} />
                  <Route path="/forum" element={<ForumPage />} />
                  <Route path="/forum/new" element={<ForumNewThreadPage />} />
                  <Route
                    path="/forum/thread/:threadId"
                    element={<ForumThreadPage />}
                  />
                  {
                  }
                  {lobbyEnabled && (
                    <Route path="/lobby" element={<LobbyPage />} />
                  )}
                  {lobbyEnabled && (
                    <Route path="/lobby/rules" element={<RoomRulesPage />} />
                  )}
                  <Route path="/review" element={<ReviewPage />} />
                  <Route path="/review/:gameId" element={<ReviewPage />} />
                  <Route path="/play/:gameId" element={<DesktopGamePage />} />
                  <Route
                    path="/mobile/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route
                    path="/village-preview/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route
                    path="/play-3d/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route
                    path="/parallel/:gameId"
                    element={<ComparisonPlayPage />}
                  />
                  <Route
                    path="/replay/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route path="/room/:code" element={<RoomPage />} />
                  <Route path="/signin" element={<SignInPage />} />
                  <Route
                    path="/forget-password"
                    element={<ForgetPasswordPage />}
                  />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route
                    path="/dashboard"
                    element={(
                      <DashboardPage
                        notificationSetupEnabled={notificationSetupEnabled}
                      />
                    )}
                  />
                  <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/legal/terms" element={<TermsOfServicePage />} />
                  <Route path="/legal/cookies" element={<CookiePolicyPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              ) : (
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  { }
                  <Route path="/new" element={<ArenaLauncherRedirect />} />
                  <Route path="/play" element={<ArenaLauncherRedirect />} />
                  <Route path="/tournaments" element={<ArenaRedirect><TournamentPage /></ArenaRedirect>} />
                  <Route path="/tournaments/:editionId" element={<ArenaRedirect><TournamentPage /></ArenaRedirect>} />
                  <Route
                    path="/tournaments/hall"
                    element={(
                      <ArenaRedirect>
                        <TournamentHall />
                      </ArenaRedirect>
                    )}
                  />
                  <Route
                    path="/play/rule"
                    element={(
                      <ArenaRedirect>
                        <GameRulesPage />
                      </ArenaRedirect>
                    )}
                  />
                  <Route
                    path="/play/roles"
                    element={(
                      <ArenaRedirect>
                        <RoleIndexPage />
                      </ArenaRedirect>
                    )}
                  />
                  <Route path="/play/:gameId" element={<DesktopGamePage />} />
                  <Route
                    path="/mobile/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route
                    path="/village-preview/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route
                    path="/players/:username"
                    element={(
                      <ArenaRedirect>
                        <PublicPlayerProfilePage />
                      </ArenaRedirect>
                    )}
                  />
                  <Route
                    path="/play-3d/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  <Route
                    path="/parallel/:gameId"
                    element={<ComparisonPlayPage />}
                  />
                  <Route
                    path="/replay/:gameId"
                    element={<LegacyPlayRouteRedirectPage />}
                  />
                  {
                  }
                  <Route path="/room/:code" element={<RoomPage />} />
                  {
                  }
                  {lobbyEnabled && (
                    <Route
                      path="/lobby"
                      element={(
                        <ArenaRedirect>
                          <LobbyPage />
                        </ArenaRedirect>
                      )}
                    />
                  )}
                  {lobbyEnabled && (
                    <Route
                      path="/lobby/rules"
                      element={(
                        <ArenaRedirect>
                          <RoomRulesPage />
                        </ArenaRedirect>
                      )}
                    />
                  )}
                  <Route path="/blogs" element={<BlogListPage />} />
                  <Route path="/blogs/:legacyLocale/:blogId" element={<BlogArticlePage />} />
                  <Route path="/blogs/:blogId" element={<BlogArticlePage />} />
                  <Route
                    path="/ai-werewolf-benchmark"
                    element={<SeoLandingPage slug="ai-werewolf-benchmark" />}
                  />
                  <Route
                    path="/mafia-ai-benchmark"
                    element={<SeoLandingPage slug="mafia-ai-benchmark" />}
                  />
                  <Route
                    path="/board-game-ai-benchmark"
                    element={<SeoLandingPage slug="board-game-ai-benchmark" />}
                  />
                  <Route
                    path="/social-deduction-ai-benchmark"
                    element={
                      <SeoLandingPage slug="social-deduction-ai-benchmark" />
                    }
                  />

                  <Route path="/signin" element={<SignInPage />} />
                  <Route
                    path="/forget-password"
                    element={<ForgetPasswordPage />}
                  />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/career" element={<CareerPage />} />
                  <Route path="/contact-us" element={<ContactPage />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/demo" element={<DemoToArenaReviewRedirect />} />
                  <Route path="/demo/:gameId" element={<DemoToArenaReviewRedirect />} />
                  {
                  }
                  <Route
                    path="/review"
                    element={(
                      <ArenaRedirect>
                        <ReviewPage />
                      </ArenaRedirect>
                    )}
                  />
                  <Route
                    path="/review/:gameId"
                    element={(
                      <ArenaRedirect>
                        <ReviewPage />
                      </ArenaRedirect>
                    )}
                  />
                  <Route path="/grpo" element={<GrpoToHomeRedirect />} />

                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/legal/terms" element={<TermsOfServicePage />} />
                  <Route path="/legal/cookies" element={<CookiePolicyPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              )}
              { }
              {registeredUserId && <UserStream />}
              {registeredUserId && <SocialChatDock />}
              <Toaster position="bottom-left" />
              <CookieBanner />
            </div>
          </AccountDataProviders>
        </AuthProvider>
      </NormalizeLegacyQueryLanguage>
    </BrowserRouter>
  )

  return (
    <SocialWorkspaceProvider userId={registeredUserId}>
      {content}
    </SocialWorkspaceProvider>
  )
}

function App() {
  const defaultTheme = isArenaHost() ? 'dark' : 'system'
  const [queryScope, setQueryScope] = useState(() => ({
    queryClient: getQueryClient(),
    generation: 0,
  }))
  const replaceAccountQueryClient = useCallback(
    (seed?: (queryClient: QueryClient) => void) => {
      const nextQueryClient = makeQueryClient()
      seed?.(nextQueryClient)
      if (typeof window !== 'undefined')
        browserQueryClient = nextQueryClient
      setQueryScope(previous =>
        replaceAccountQueryScope(previous, nextQueryClient),
      )
    },
    [],
  )
  const { queryClient, generation } = queryScope

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme={defaultTheme}
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider key={generation} client={queryClient}>
        <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
          <AppContent replaceAccountQueryClient={replaceAccountQueryClient} />
        </TRPCProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
